/**
 * Extracts the domain from an address if an @ symbol exists and isn't at the start or end of the address.
 * @param {string} addressString The address string to extract the domain from (if it exists).
 * @returns {string} The extracted domain. Empty string of none found.
 */
function extractDomainFromAddress (addressString) {
  const indexOfAtSymbol = addressString.lastIndexOf('@')
  if (indexOfAtSymbol !== 0 && indexOfAtSymbol !== addressString.length - 1) {
    // If '@' symbol found in middle of addressString, split it.
    if (indexOfAtSymbol !== -1) {
      return addressString.substr(indexOfAtSymbol + 1)
    }
  }
  return ''
}

/**
 * Determines which address and domain to use depending on whether the address string contains a domain or not.
 * @param {string} addressString The address string to examine and extract a domain from (if any).
 * @param {string} defaultDomainString The domain to use if the address string does not contain a domain in it.
 * @returns {Object} An object containing the correct address and domain to use.
 */
function separateAddressAndDomain (addressString, defaultDomainString) {
  const extractedDomain = extractDomainFromAddress(addressString)
  // If a domain was extracted from the address, use that as the domain and strip it from the address.
  if (extractedDomain) {
    return {
      address: addressString.substr(0, addressString.length - extractedDomain.length - 1),
      domain: extractedDomain
    }
  } else {
    return {
      address: addressString,
      domain: defaultDomainString
    }
  }
}

/**
 * Extracts any pre-pended data before a ":" (if it exists) from the beginning of a string.
 * @param {string} inputString The string to remove pre-pended data from.
 * @returns {string} The pre-pended data string.
 */
function extractPrependedData (inputString) {
  const prependedDataMatches = inputString.match(/^.*:/g)
  if (prependedDataMatches && prependedDataMatches[0]) {
    return prependedDataMatches[0]
  } else {
    return ''
  }
}

/**
 * Finds the leading special characters ("#", "+", "*") of an address if it is a phone number.
 * If the address contains letters or any non-visual-separator characters,
 *  it is not a phone number and no leading special characters will be found.
 * @param {string} addressString The address string to find the leading characters of (if any).
 *  The addressString must not contain any pre-pended data such as "sip:".
 *  The addressString must not contain a domain.
 * @returns {string} The leading special characters as one string. Empty string if none found.
 */
function extractLeadingSpecialCharacters (addressString) {
  // A single or group of contiguous characters are considered leading character/s if it is  the following:
  // - starts at the beginning of the string - ^
  // - is any of the following characters - [#+*]+
  // - is followed by a digit or "(" - [\d|(]
  // - is followed by any number of only digits and visual separators - [\d \-.()+]*$
  const potentialLeadingChars = addressString.match(/^[#+*]+[\d|(][\d \-.()+]*$/g)
  if (potentialLeadingChars && potentialLeadingChars[0]) {
    // Guaranteed to have a match for regex [#+*]+ since we have potentialLeadingChars
    // which was a match for a similar regex and we are simply extracting the leading characters part.
    const actualLeadingChars = potentialLeadingChars[0].match(/[#+*]+/g)
    return actualLeadingChars[0]
  }
  return ''
}

/**
 * Outputs a new string without its phone number visual separators ("-", ".", "(", ")", "+").
 * @param {string} inputString The string to remove visual separators from.
 * @returns {string} A new string without visual separators.
 */
function withoutVisualSeparators (inputString) {
  return inputString.replace(/[ \-.()+]/g, '')
}

/**
 * Determines whether a string should be considered a phone number or not.
 * @param {string} addressString The address string to check.
 *  The addressString must not contain any pre-pended data such as "sip:"
 *  The addressString must not contain any leading special characters.
 *  The addressString must not contain a domain.
 * @returns {boolean} True if the input string is a phone number. False if it is not.
 */
function isPhoneNumber (addressString) {
  const cleanNumber = withoutVisualSeparators(addressString)
  const phoneNumberMatch = cleanNumber.match(/^\d+$/g)
  return phoneNumberMatch && phoneNumberMatch.length === 1
}

/**
 * Processes the address string and returns the correct output.
 * If the address is a phone number, visual separators are removed.
 * Otherwise, it will just return the address as-is.
 * @param {string} addressString The address string to process.
 *  The addressString must not contain any pre-pended data such as "sip:".
 *  The addressString must not contain any leading special characters (if it is a phone number).
 *  The addressString must not contain a domain.
 * @returns {string} A phone number without visual-separators or the addressString as-is.
 */
function processAddress (addressString) {
  return isPhoneNumber(addressString) ? withoutVisualSeparators(addressString) : addressString
}

/**
 * Processes the domain string and returns the correct output.
 * Adds an "@" symbol if it isn't present at the beginning of the domain.
 * @param {string} domainString The domain string to process.
 * @returns {string} The domain with "@" symbol at the beginning if it doesn't exist.
 */
function processDomain (domainString) {
  if (domainString === '' || domainString === undefined) {
    return ''
  } else if (domainString.indexOf('@') === 0) {
    return domainString
  } else {
    return '@' + domainString
  }
}

/**
 *The function takes in the input dial string and domain address of the user, performs a normalization process based on the phone number handling normalization rules
 * @function normalizeSipUri
 * @param {string} address   It contains the input dial string the user dials in or the callee address
 * @param {string} domain    It contains the user's domain address
 * @returns {string} output  The output which is the normalized callee address/phone number
 */
export function normalizeSipUri (address, domain) {
  // Remove leading and trailing white spaces.
  address = address.trim()

  // Extract domain.
  const resultingAddressAndDomain = separateAddressAndDomain(address, domain)
  domain = resultingAddressAndDomain.domain
  address = resultingAddressAndDomain.address

  // Extract pre-pended "sip:".
  const prepend = extractPrependedData(address)
  address = address.substr(prepend.length)

  // Extract leading characters.
  const leadingChars = extractLeadingSpecialCharacters(address)
  address = address.substr(leadingChars.length)

  // Process and build parts into final output in the form of `<prepend>:<leadingChars><address>@<domain>`.
  return 'sip:' + leadingChars + processAddress(address) + processDomain(domain)
}
